import { useMemo, useState, type JSX, type ReactNode } from "react";
import {
  Button,
  Card,
  DataTable,
  Inline,
  Stack,
  StatePill,
  type DataTableColumn,
  type StatePillTone,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";

import {
  brandingTally,
  isForbidden,
  isHexColor,
  logoKind,
  sortTenants,
  tenantLabel,
  useGtccBrandingTenants,
  type GtccBrandingTenant,
  type GtccLogoKind,
} from "./useGtccBranding.js";
import { GtccBrandingEditor } from "./GtccBrandingEditor.js";

/**
 * B-GTCC-A19 — register row 27 ("Branding und Tenancy Settings",
 * ticket E-GTCC-15). Read by B-GTCC-A19, written by A-GTCC-27-WRITE2.
 *
 * The order named the gtcc1 branding page as the template. Its shape
 * carried over; its data source did not, and the reason is measured:
 * `GET /v1/tenancy/settings` resolves the row by the CALLING tenant
 * (`tenancy/settings.service.ts:56`), so it cannot answer "je Mandant"
 * — the register's own remark on row 27 is "Tenant-Grenze". The values
 * for every tenant do exist in one platform-owner request, and that is
 * what this page reads; the reasoning sits in `useGtccBranding.ts`.
 *
 * Two consequences are stated on the page rather than left implicit:
 *
 *   - Unset is not empty. Every key of `tenantSettingsSchema` is
 *     optional and `tenants.settings` defaults to `{}`, so a column
 *     with no value says "not set" instead of going blank.
 *
 *   - The logo is not rendered. It may be a data URI of up to 200 KB
 *     (`tenancy/settings.dto.ts:44`); the page reports which kind is
 *     stored and leaves the image to the branding page.
 *
 * A-GTCC-27-WRITE2 (Owner-GO 2026-09-21) — the branding and tenancy
 * settings of ONE target tenant are editable: each row of the branding
 * table carries an edit button that opens `GtccBrandingEditor` for
 * exactly that tenant, covering the values of BOTH tables plus the
 * vehicle visibility; saving goes through a confirmation naming the
 * tenant by name and ID and then `PATCH /v1/admin/tenants/:tenantId/
 * branding`. The tenant-scoped `/v1/tenancy/settings` is still never
 * touched (it would write the CALLING tenant). KEIN Upload: the logo is
 * written as an https link only; the upload is its own package.
 */

const LOGO_TONE: Record<GtccLogoKind, StatePillTone> = {
  none: "neutral",
  linked: "info",
  embedded: "info",
};

const VISIBILITY_TONE: Record<string, StatePillTone> = {
  all_visible: "neutral",
  restricted: "info",
};

/* A fresh `[]` on every render would change the table props identity
   while the query is loading or failed, for nothing. */
const EMPTY_TENANTS: readonly GtccBrandingTenant[] = [];

function SectionCard({
  testId,
  headingId,
  title,
  subtitle,
  children,
}: {
  readonly testId: string;
  readonly headingId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <section aria-labelledby={headingId} data-testid={testId}>
      <Card elevation={1}>
        <Stack gap={3}>
          <Stack gap={1}>
            <h3 id={headingId} style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md)" }}>
              {title}
            </h3>
            <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>{subtitle}</p>
          </Stack>
          {children}
        </Stack>
      </Card>
    </section>
  );
}

export function GtccBrandingView(): JSX.Element {
  const { t, locale } = useLocale();
  const query = useGtccBrandingTenants();
  const [editId, setEditId] = useState<string | null>(null);

  const rows = useMemo(
    () => sortTenants(query.data?.items ?? EMPTY_TENANTS, locale),
    [query.data, locale],
  );
  const tally = useMemo(() => brandingTally(rows), [rows]);
  // Looked up by id on every render, so a refetch after a save hands the
  // editor the stored values, not the ones it was opened with.
  const editing = editId === null ? null : (rows.find((row) => row.id === editId) ?? null);

  const unset = t("admin.gtcc.branding.value.unset");

  const error = query.isError
    ? {
        title: isForbidden(query.error)
          ? t("admin.gtcc.branding.forbidden")
          : t("admin.gtcc.branding.error"),
      }
    : null;

  const tenantCell = (row: GtccBrandingTenant): ReactNode => (
    <Stack gap={0}>
      <span>{tenantLabel(row)}</span>
      {row.slug ? <span style={{ color: "var(--gt-sys-text-muted)" }}>{row.slug}</span> : null}
    </Stack>
  );

  const brandingColumns: readonly DataTableColumn<GtccBrandingTenant>[] = [
    {
      id: "tenant",
      header: t("admin.gtcc.branding.column.tenant"),
      cell: tenantCell,
    },
    {
      id: "logo",
      header: t("admin.gtcc.branding.column.logo"),
      cell: (row) => {
        const kind = logoKind(row.settings?.logoUrl);
        return (
          <StatePill tone={LOGO_TONE[kind]} label={t(`admin.gtcc.branding.value.logo.${kind}`)} />
        );
      },
    },
    {
      id: "accent",
      header: t("admin.gtcc.branding.column.accent"),
      cell: (row) => {
        const accent = row.settings?.accentColor;
        if (!accent) return unset;
        if (!isHexColor(accent)) return accent;
        return (
          <Inline gap={2}>
            <span
              aria-hidden="true"
              style={{
                display: "inline-block",
                width: "1rem",
                height: "1rem",
                borderRadius: "var(--gt-sys-radius-sm)",
                border: "1px solid var(--gt-sys-border)",
                backgroundColor: accent,
              }}
            />
            <span>{accent}</span>
          </Inline>
        );
      },
    },
    {
      id: "locale",
      header: t("admin.gtcc.branding.column.locale"),
      cell: (row) => row.settings?.defaultLocale ?? unset,
    },
    {
      id: "contact",
      header: t("admin.gtcc.branding.column.contact"),
      cell: (row) => row.settings?.contactEmail ?? unset,
    },
    {
      id: "edit",
      header: t("admin.gtcc.branding.column.edit"),
      cell: (row) => (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setEditId(row.id)}
          aria-label={t("admin.gtcc.branding.edit.open_for", { name: tenantLabel(row) })}
          data-testid={`gtcc-branding-edit-open-${row.id}`}
        >
          {t("admin.gtcc.branding.edit.open")}
        </Button>
      ),
    },
  ];

  const tenancyColumns: readonly DataTableColumn<GtccBrandingTenant>[] = [
    {
      id: "tenant",
      header: t("admin.gtcc.branding.column.tenant"),
      cell: tenantCell,
    },
    {
      id: "timezone",
      header: t("admin.gtcc.branding.column.timezone"),
      cell: (row) => row.settings?.timezone ?? unset,
    },
    {
      id: "idle_cap",
      header: t("admin.gtcc.branding.column.idle_cap"),
      cell: (row) => {
        const minutes = row.settings?.sessionIdleCapMinutes;
        return minutes === undefined ? unset : t("admin.gtcc.branding.value.idle_cap", { minutes });
      },
    },
    {
      id: "preset",
      header: t("admin.gtcc.branding.column.preset"),
      cell: (row) => row.settings?.regionalPreferences?.preset ?? unset,
    },
    {
      id: "demo",
      header: t("admin.gtcc.branding.column.demo"),
      cell: (row) => {
        const on = row.settings?.demoApplyAllowed === true;
        return (
          <StatePill
            tone={on ? "warn" : "neutral"}
            label={t(on ? "admin.gtcc.branding.value.on" : "admin.gtcc.branding.value.off")}
          />
        );
      },
    },
    {
      id: "onboarding",
      header: t("admin.gtcc.branding.column.onboarding"),
      cell: (row) => {
        const done = Boolean(row.settings?.onboarding?.firstRunCompletedAt);
        return (
          <StatePill
            tone={done ? "success" : "neutral"}
            label={t(
              done
                ? "admin.gtcc.branding.value.onboarding.done"
                : "admin.gtcc.branding.value.onboarding.open",
            )}
          />
        );
      },
    },
    {
      id: "visibility",
      header: t("admin.gtcc.branding.column.visibility"),
      cell: (row) => {
        const mode = row.visibilityMode ?? "";
        const tone = VISIBILITY_TONE[mode] ?? "neutral";
        const label = VISIBILITY_TONE[mode]
          ? t(`admin.gtcc.branding.value.visibility.${mode}`)
          : t("admin.gtcc.branding.value.visibility.unknown");
        return <StatePill tone={tone} label={label} />;
      },
    },
  ];

  return (
    <Stack gap={4} data-testid="gtcc-view-branding" style={{ padding: "var(--gt-sys-space-5)" }}>
      <Stack gap={1}>
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg)" }}>
          {t("admin.gtcc.branding.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted)" }}>
          {t("admin.gtcc.branding.subtitle")}
        </p>
        <Inline gap={2}>
          <StatePill tone="info" label={t("admin.gtcc.branding.write_scope")} />
          <StatePill tone="info" label={t("admin.gtcc.branding.scope")} />
        </Inline>
      </Stack>

      {query.isSuccess ? (
        <Inline gap={2} data-testid="gtcc-branding-summary">
          <StatePill
            tone="info"
            label={t("admin.gtcc.branding.summary.tenants", { count: tally.tenants })}
          />
          <StatePill
            tone="neutral"
            label={t("admin.gtcc.branding.summary.logo", {
              count: tally.logo,
              total: tally.tenants,
            })}
          />
          <StatePill
            tone="neutral"
            label={t("admin.gtcc.branding.summary.accent", {
              count: tally.accent,
              total: tally.tenants,
            })}
          />
          <StatePill
            tone="neutral"
            label={t("admin.gtcc.branding.summary.contact", {
              count: tally.contact,
              total: tally.tenants,
            })}
          />
          <StatePill
            tone="neutral"
            label={t("admin.gtcc.branding.summary.timezone", {
              count: tally.timezone,
              total: tally.tenants,
            })}
          />
        </Inline>
      ) : null}

      {editing !== null ? (
        <GtccBrandingEditor key={editing.id} tenant={editing} onClose={() => setEditId(null)} />
      ) : null}

      <SectionCard
        testId="gtcc-branding-values"
        headingId="gtcc-branding-values-heading"
        title={t("admin.gtcc.branding.branding.title")}
        subtitle={t("admin.gtcc.branding.branding.subtitle")}
      >
        <DataTable
          caption={t("admin.gtcc.branding.branding.caption")}
          columns={brandingColumns}
          rows={rows}
          getRowId={(row) => row.id}
          loading={query.isPending}
          loadingLabel={t("admin.gtcc.branding.loading")}
          error={error}
          empty={{ title: t("admin.gtcc.branding.empty") }}
          testId="gtcc-branding-values-table"
        />
      </SectionCard>

      <SectionCard
        testId="gtcc-branding-tenancy"
        headingId="gtcc-branding-tenancy-heading"
        title={t("admin.gtcc.branding.tenancy.title")}
        subtitle={t("admin.gtcc.branding.tenancy.subtitle")}
      >
        <DataTable
          caption={t("admin.gtcc.branding.tenancy.caption")}
          columns={tenancyColumns}
          rows={rows}
          getRowId={(row) => row.id}
          loading={query.isPending}
          loadingLabel={t("admin.gtcc.branding.loading")}
          error={error}
          empty={{ title: t("admin.gtcc.branding.empty") }}
          testId="gtcc-branding-tenancy-table"
        />
      </SectionCard>

      <SectionCard
        testId="gtcc-branding-note"
        headingId="gtcc-branding-note-heading"
        title={t("admin.gtcc.branding.note.title")}
        subtitle={t("admin.gtcc.branding.note.subtitle")}
      >
        <p style={{ margin: 0 }}>{t("admin.gtcc.branding.note.scope")}</p>
        <p style={{ margin: 0 }}>{t("admin.gtcc.branding.note.logo")}</p>
        <p style={{ margin: 0 }}>{t("admin.gtcc.branding.note.entitlements")}</p>
      </SectionCard>
    </Stack>
  );
}
